import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  RawSharedCollectionResponse,
  SharedCollectionError,
  SharedCollectionResponse,
  UseSharedCollectionState,
} from "../../../types/public-shared-collection";

export type UseSharedCollectionOptions = {
  baseUrl?: string;
  enabled?: boolean;
  retryCount?: number;
  retryDelay?: number;
  onSuccess?: (data: SharedCollectionResponse) => void;
  onError?: (error: SharedCollectionError) => void;
};

export type UseSharedCollectionReturn = UseSharedCollectionState & {
  refetch: () => Promise<void>;
  mutate: (data: SharedCollectionResponse | null) => void;
};

const ERROR_MESSAGE_MAP: Record<string, string> = {
  method_not_allowed: "Request method not allowed",
  invalid_access_token: "Invalid or missing access token",
  not_found: "Collection not found",
  link_suspended: "This shared link has been suspended",
  link_not_available: "This shared link is not yet available",
  link_expired: "This shared link has expired",
  internal_server_error: "An internal server error occurred",
};

const DEFAULT_OPTIONS: Required<UseSharedCollectionOptions> = {
  baseUrl: `${process.env.REACT_APP_BACKEND_API_URL}/share/collection/public`,
  enabled: true,
  retryCount: 3,
  retryDelay: 1000,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSuccess: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onError: () => {},
};

export const useSharedCollection = (
  accessToken: string | null,
  options: UseSharedCollectionOptions = {}
): UseSharedCollectionReturn => {
  const opts = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...options }),
    [
      options.baseUrl,
      options.enabled,
      options.retryCount,
      options.retryDelay,
      options.onSuccess,
      options.onError,
    ]
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<UseSharedCollectionState>({
    data: null,
    loading: false,
    error: null,
    isValidating: false,
  });

  const sleep = useCallback(
    (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms)),
    []
  );

  const transformResponse = useCallback(
    (rawData: RawSharedCollectionResponse): SharedCollectionResponse => {
      return {
        ...rawData,
        assets: rawData.assets.map((asset) => ({
          ...asset,
          attributes: new Map(asset.attributes.value || []),
        })),
      };
    },
    []
  );

  const fetchCollection = useCallback(
    async (
      token: string,
      isBackground = false
    ): Promise<SharedCollectionResponse> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const url = new URL(opts.baseUrl);
      url.searchParams.set("access_token", token);

      let lastError: SharedCollectionError | null = null;

      for (let attempt = 1; attempt <= opts.retryCount; attempt++) {
        try {
          const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          });

          const rawData = await response.json();

          if (!response.ok) {
            const error: SharedCollectionError = {
              code: rawData.code || response.status,
              message: rawData.message || response.statusText,
            };

            if (response.status < 500) {
              throw error;
            }

            lastError = error;

            if (attempt < opts.retryCount) {
              await sleep(opts.retryDelay * attempt);
              continue;
            }

            throw error;
          }

          return transformResponse(rawData as RawSharedCollectionResponse);
        } catch (error) {
          if (error instanceof Error && error.name === "AbortError") {
            throw error;
          }

          if (error instanceof Error && !("code" in error)) {
            lastError = {
              code: 0,
              message: "Network error occurred",
            };
          } else {
            lastError = error as SharedCollectionError;
          }

          if (attempt < opts.retryCount) {
            await sleep(opts.retryDelay * attempt);
            continue;
          }

          throw lastError;
        }
      }

      throw lastError;
    },
    [opts.baseUrl, opts.retryCount, opts.retryDelay, sleep, transformResponse]
  );

  const refetch = useCallback(async (): Promise<void> => {
    if (!accessToken) return;

    setState((prev) => ({
      ...prev,
      isValidating: true,
      error: null,
    }));

    try {
      const data = await fetchCollection(accessToken, true);

      setState((prev) => ({
        ...prev,
        data,
        isValidating: false,
        error: null,
      }));

      opts.onSuccess(data);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      const apiError = error as SharedCollectionError;
      const enhancedError = {
        ...apiError,
        message: ERROR_MESSAGE_MAP[apiError.message] || apiError.message,
      };

      setState((prev) => ({
        ...prev,
        error: enhancedError,
        isValidating: false,
      }));

      opts.onError(enhancedError);
    }
  }, [accessToken, fetchCollection, opts.onSuccess, opts.onError]);

  const mutate = useCallback(
    (newData: SharedCollectionResponse | null): void => {
      setState((prev) => ({
        ...prev,
        data: newData,
        error: null,
      }));
    },
    []
  );

  useEffect(() => {
    if (!accessToken || !opts.enabled) {
      setState({
        data: null,
        loading: false,
        error: null,
        isValidating: false,
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    let isCancelled = false;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const performFetch = async (): Promise<void> => {
      try {
        const url = new URL(opts.baseUrl);
        url.searchParams.set("access_token", accessToken);

        let lastError: SharedCollectionError | null = null;

        for (let attempt = 1; attempt <= opts.retryCount; attempt++) {
          try {
            const response = await fetch(url.toString(), {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              signal: controller.signal,
            });

            const rawData = await response.json();

            if (!response.ok) {
              const error: SharedCollectionError = {
                code: rawData.code || response.status,
                message: rawData.message || response.statusText,
              };

              if (response.status < 500) {
                throw error;
              }

              lastError = error;

              if (attempt < opts.retryCount) {
                await sleep(opts.retryDelay * attempt);
                continue;
              }

              throw error;
            }

            const transformedData = transformResponse(
              rawData as RawSharedCollectionResponse
            );

            if (!isCancelled) {
              setState({
                data: transformedData,
                loading: false,
                error: null,
                isValidating: false,
              });

              opts.onSuccess(transformedData);
            }
            return;
          } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
              return;
            }

            if (error instanceof Error && !("code" in error)) {
              lastError = {
                code: 0,
                message: "Network error occurred",
              };
            } else {
              lastError = error as SharedCollectionError;
            }

            if (attempt < opts.retryCount) {
              await sleep(opts.retryDelay * attempt);
              continue;
            }

            throw lastError;
          }
        }

        throw lastError;
      } catch (error) {
        if (
          !isCancelled &&
          !(error instanceof Error && error.name === "AbortError")
        ) {
          const apiError = error as SharedCollectionError;
          const enhancedError = {
            ...apiError,
            message: ERROR_MESSAGE_MAP[apiError.message] || apiError.message,
          };

          setState({
            data: null,
            loading: false,
            error: enhancedError,
            isValidating: false,
          });

          opts.onError(enhancedError);
        }
      }
    };

    performFetch();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [
    accessToken,
    opts.enabled,
    opts.baseUrl,
    opts.retryCount,
    opts.retryDelay,
    opts.onSuccess,
    opts.onError,
    sleep,
    transformResponse,
  ]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    ...state,
    refetch,
    mutate,
  };
};
